import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Star, MessageSquare, MessageCircle, Settings, Calendar as CalendarIcon, Shield, Eye, Phone, Users, ChevronDown, ChevronUp, ChevronRight, CalendarCheck, Dog, Camera, MapPin, Lock, Clock, UserCircle, Home, PawPrint, Briefcase, X, ImagePlus, Inbox, CheckCircle, XCircle, Crown, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { es } from 'date-fns/locale';
import { format } from 'date-fns';
import { toast } from 'sonner';
import api from '@/lib/api';
import ProviderBookings from '@/components/ProviderBookings';
import CareRequestsProvider from '@/components/CareRequestsProvider';
import DashboardHero from '@/components/provider-dashboard/DashboardHero';
import DashboardStats from '@/components/provider-dashboard/DashboardStats';
import DashboardProfileAlert from '@/components/provider-dashboard/DashboardProfileAlert';
import DashboardPremium from '@/components/provider-dashboard/DashboardPremium';
import SubscriptionCard from '@/components/SubscriptionCard';
import ProviderGallery from '@/components/ProviderGallery';
import ServiceZones from '@/components/ServiceZones';

const ProviderDashboard = () => {
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alwaysActive, setAlwaysActive] = useState(true);
  const [availableDates, setAvailableDates] = useState([]);
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [showPremiumBanner, setShowPremiumBanner] = useState(false);
  const [sosConfig, setSosConfig] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [givenReviews, setGivenReviews] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, punctuality: 5, pet_behavior: 5, communication: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [activeTab, setActiveTab] = useState('bookings');
  const [hasSubscription, setHasSubscription] = useState(false);
  const [contactRequests, setContactRequests] = useState([]);
  const [publishedRequestsCount, setPublishedRequestsCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [respondingTo, setRespondingTo] = useState(null);
  const [personalInfo, setPersonalInfo] = useState({
    housing_type: '', has_yard: false, yard_description: '',
    has_own_pets: false, own_pets_description: '',
    animal_experience: '', daily_availability: '', additional_info: '',
    yard_photos: [], pets_photos: []
  });
  const [savingPersonalInfo, setSavingPersonalInfo] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [profileForm, setProfileForm] = useState({
    business_name: '', description: '', phone: '', address: '', comuna: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [editingServices, setEditingServices] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [profileRes, subRes] = await Promise.all([
        api.get('/providers/my-profile'),
        api.get('/subscription/my').catch(() => ({ data: { has_subscription: false } }))
      ]);
      const p = profileRes.data;
      setProvider(p);
      setProfileForm({
        business_name: p.business_name || '',
        description: p.description || '',
        phone: p.phone || '',
        address: p.address || '',
        comuna: p.comuna || ''
      });
      setHasSubscription(subRes.data.has_subscription || subRes.data.status === 'active');
      setAlwaysActive(p.always_active !== false);
      setAvailableDates((p.available_dates || []).map(d => new Date(d)));
      try {
        const revRes = await api.get(`/providers/${p.provider_id}/reviews`);
        setReviews(revRes.data);
      } catch {}
      try {
        const sosRes = await api.get('/sos/info');
        setSosConfig(sosRes.data);
      } catch {}
      try {
        const [convRes, givenRes] = await Promise.all([
          api.get('/chat/conversations'),
          api.get('/reviews/provider/given')
        ]);
        setConversations(convRes.data);
        setGivenReviews(givenRes.data);
      } catch {}
      try {
        const piRes = await api.get('/providers/my-profile/personal-info');
        if (piRes.data && Object.keys(piRes.data).length > 0) {
          setPersonalInfo(prev => ({ ...prev, ...piRes.data }));
        }
      } catch {}
      try {
        const crRes = await api.get('/contact-requests/received');
        setContactRequests(crRes.data);
      } catch {}

      try {
        const careReqRes = await api.get('/care-requests');
        setPublishedRequestsCount(careReqRes.data.length);
      } catch {}

      try {
        const [convRes, notifRes] = await Promise.all([
          api.get('/chat/conversations').catch(() => ({ data: [] })),
          api.get('/notifications').catch(() => ({ data: [] }))
        ]);

        const chatUnread = (convRes.data || []).reduce((sum, conv) => {
          return sum + (conv.unread_count || 0);
        }, 0);

        const messageNotificationsUnread = (notifRes.data || []).filter(n => {
          const title = (n.title || '').toLowerCase();
          const message = (n.message || '').toLowerCase();
          return !n.read && (title.includes('mensaje') || message.includes('mensaje'));
        }).length;

        setUnreadMessages(Math.max(chatUnread, messageNotificationsUnread));
      } catch {
        setUnreadMessages(0);
      }
    } catch {
      navigate('/provider/register');
    } finally {
      setLoading(false);
    }
  };

  const saveAvailability = async () => {
    setSavingAvailability(true);
    try {
      await api.put('/providers/my-profile', {
        always_active: alwaysActive,
        available_dates: alwaysActive ? [] : availableDates.map(d => d.toISOString())
      });
      toast.success('Disponibilidad actualizada');
    } catch (e) {
      toast.error('Error al actualizar');
    } finally {
      setSavingAvailability(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-16 h-16 border-4 border-[#E6202E] border-t-transparent rounded-full" /></div>;

  if (!provider) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <p className="text-gray-600 mb-6">No tienes perfil de cuidador.</p>
        <Link to="/provider/register"><Button className="bg-[#E6202E] hover:bg-[#D31522]">Crear Perfil</Button></Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="provider-dashboard">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <DashboardHero
          provider={provider}
          contactRequests={contactRequests}
          publishedRequestsCount={publishedRequestsCount}
          unreadMessages={unreadMessages}
          setActiveTab={setActiveTab}
        />

        <DashboardStats
          provider={provider}
          alwaysActive={alwaysActive}
          availableDates={availableDates}
        />

        <DashboardProfileAlert
          provider={provider}
          setActiveTab={setActiveTab}
        />

        <DashboardPremium
          hasSubscription={hasSubscription}
          showPremiumBanner={showPremiumBanner}
          setShowPremiumBanner={setShowPremiumBanner}
        />

        <div className="mt-6 flex items-center justify-center gap-4">

          <Link to={`/provider/${provider.provider_id}`}>
            <Button variant="outline" className="border-[#E6202E] text-[#E6202E]"><Eye className="w-4 h-4 mr-2" />Ver mi perfil publico</Button>
          </Link>
          <Link to="/historial">
            <Button variant="outline" className="border-gray-300 text-gray-600" data-testid="history-link-provider"><Clock className="w-4 h-4 mr-2" />Historial y Facturas</Button>
          </Link>
        </div>

        {/* SOS Emergency Button - Visible for all, functional only for subscribed AND within schedule */}
        {sosConfig?.active && sosConfig?.phone && (
          <div className={`mt-8 rounded-2xl p-6 text-white ${
            hasSubscription && sosConfig.is_available 
              ? 'bg-gradient-to-r from-red-600 to-red-700' 
              : 'bg-gradient-to-r from-gray-400 to-gray-500'
          }`} data-testid="sos-section">
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                hasSubscription && sosConfig.is_available ? 'bg-white/20 animate-pulse' : 'bg-white/10'
              }`}>
                <Phone className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  Emergencia SOS
                  {(!hasSubscription || !sosConfig.is_available) && <Lock className="w-5 h-5" />}
                </h3>
                <p className="text-sm opacity-90">
                  {hasSubscription 
                    ? (sosConfig.vet_name ? `Veterinario: ${sosConfig.vet_name}` : 'Asistencia veterinaria de emergencia')
                    : 'Acceso a veterinario de emergencia'
                  }
                </p>
                <p className="text-xs opacity-75 mt-1">
                  Horario: {sosConfig.schedule_text || sosConfig.schedule || '8:00 - 20:00 hrs'}
                  {hasSubscription && !sosConfig.is_available && (
                    <span className="ml-2 px-2 py-0.5 bg-white/20 rounded">Fuera de horario</span>
                  )}
                </p>
              </div>
              {hasSubscription ? (
                sosConfig.is_available ? (
                  <a 
                    href={`https://wa.me/${sosConfig.phone.replace(/\D/g, '')}?text=${encodeURIComponent('Hola, soy cuidador de U-CAN y tengo una emergencia veterinaria con un perro a mi cargo. ¿Tienen disponibilidad?')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="sos-whatsapp-button"
                  >
                    <Button className="bg-white text-green-600 hover:bg-green-50 font-bold text-lg px-6 py-5">
                      <MessageCircle className="w-5 h-5 mr-2" /> WhatsApp
                    </Button>
                  </a>
                ) : (
                  <div className="text-center">
                    <p className="text-xs opacity-75 mb-2">Fuera de horario</p>
                    <Button 
                      className="bg-white/20 text-white border border-white/40 font-bold px-6 py-5 cursor-not-allowed"
                      disabled
                    >
                      <Clock className="w-5 h-5 mr-2" /> No disponible
                    </Button>
                  </div>
                )
              ) : (
                <div className="text-center">
                  <p className="text-xs opacity-75 mb-2">Solo para suscritos</p>
                  <Button 
                    className="bg-white/20 text-white border border-white/40 font-bold px-6 py-5 cursor-not-allowed"
                    disabled
                  >
                    <Lock className="w-5 h-5 mr-2" /> Bloqueado
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProviderDashboard;
